import { useIntegrationApp } from "@membranehq/react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSalesforceIntegration, useSyncConfigurations } from "../../../hooks/QueryHooks";
import { getProvider } from "../../../integrations/providers";
import IntegrationDetailShell from "../../home/settings/integrations/components/IntegrationDetailShell";
import useWorkspaceStore from "../../../store/workspace/workspaceStore";
import { MappingStudio } from "../shared/mapping-studio";
import ProviderNewFlow from "../shared/new-flow/ProviderNewFlow";

const SalesforcePage = () => {
  const workspace: any = useWorkspaceStore((state) => state.workspace);
  const integrationApp = useIntegrationApp();
  const provider = getProvider("salesforce");
  const navigate = useNavigate();
  const useStabilizedMappingStudio = (import.meta.env.VITE_ENABLE_CRM_MAPPING_STUDIO_STABILIZATION ?? "true") !== "false";
  const activatableObjects = useMemo(
    () => [...(provider.activatableObjects || [])],
    [provider.activatableObjects],
  );

  const [integrationLoading, setIntegrationLoading] = useState<boolean>(false);
  const [connectionData, setConnectionData] = useState<any>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [connectedAt, setConnectedAt] = useState<string | null>(null);
  const [isCheckingConnection, setIsCheckingConnection] = useState<boolean>(true);
  const [showMappingStudio, setShowMappingStudio] = useState<boolean>(false);

  const { data: salesforceIntegration }: any = useSalesforceIntegration({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  const { data: syncConfigurations }: any = useSyncConfigurations({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id && !!salesforceIntegration?.length,
  });

  const salesforceSyncConfigurations = useMemo(() => (
    syncConfigurations?.filter((config: any) => config && config.integration_id === salesforceIntegration?.[0]?.id) || []
  ), [salesforceIntegration, syncConfigurations]);

  useEffect(() => {
    const checkConnection = async () => {
      try {
        const integration = await integrationApp.integration(provider.key).get();
        const existingConnection = integration?.connection;
        if (existingConnection && !existingConnection.disconnected) {
          setConnectionData({ ...integration, connectionId: existingConnection?.id || integration?.id });
          setIsConnected(true);
          const timestamp = (existingConnection as any)?.connectedAt
            || (integration as any)?.createdAt
            || (integration as any)?.updatedAt
            || new Date().toISOString();
          setConnectedAt(timestamp);
        }
      } catch {
        // no existing connection
      } finally {
        setIsCheckingConnection(false);
      }
    };

    checkConnection().catch(() => {
      setIsCheckingConnection(false);
    });
  }, [integrationApp, provider.key]);

  const ensureInfra = async () => {
    try {
      await Promise.allSettled(
        activatableObjects.map((slug) =>
          integrationApp.connection(provider.key).dataSource(slug).get({ autoCreate: true }),
        ),
      );

      await Promise.allSettled(
        activatableObjects.map((slug) =>
          integrationApp.connection(provider.key).fieldMapping(slug).get({ autoCreate: true }),
        ),
      );

      const firstSyncFlows = Array.from(
        new Set(
          activatableObjects
            .map((slug) => provider.flowKeys.firstSync?.[slug])
            .filter((key): key is string => Boolean(key)),
        ),
      );
      await Promise.allSettled(
        firstSyncFlows.map((flowKey) =>
          integrationApp.connection(provider.key).flow(flowKey).get({ autoCreate: true }),
        ),
      );
    } catch (error) {
      // best-effort provisioning
      console.error("Failed to ensure Salesforce data sources/field mappings", error);
    }
  };

  const handleConnectOrReconnect = async () => {
    setIntegrationLoading(true);
    try {
      const connection = isConnected
        ? await integrationApp.integration(provider.key).openNewConnection()
        : await integrationApp.integration(provider.key).connect();

      if (connection && (connection as any).state === "READY") {
        setConnectionData(connection);
        setIsConnected(true);
        setConnectedAt(new Date().toISOString());
        await ensureInfra();
      }
    } catch (error) {
      console.error("Salesforce connection failed:", error);
    } finally {
      setIntegrationLoading(false);
    }
  };

  const handleBack = () => {
    if (!workspace?.unique_identifier) {
      navigate(-1);
      return;
    }
    navigate(`/home/${workspace.unique_identifier}/settings/integrations`);
  };

  const handleOpenMappingStudio = () => {
    setShowMappingStudio(true);
  };

  const connectedDateLabel = connectedAt
    ? new Date(connectedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;

  // Show Mapping Studio as full-page view when opened
  if (showMappingStudio) {
    return (
      <div className="max-w-4xl mx-auto py-6 px-4">
        {isCheckingConnection ? (
          <div className="py-10 text-center text-sm text-[var(--mantine-color-dimmed)]">Checking connection...</div>
        ) : (
          useStabilizedMappingStudio ? (
            <MappingStudio
              providerKey="salesforce"
              isConnected={isConnected}
              connectionId={connectionData?.connectionId || connectionData?.id}
              onBack={() => setShowMappingStudio(false)}
              onComplete={() => setShowMappingStudio(false)}
            />
          ) : (
            <ProviderNewFlow
              providerKey="salesforce"
              canonicalObjectSlugs={activatableObjects}
              existingSyncConfigurations={salesforceSyncConfigurations}
              isConnected={isConnected}
              connectionId={connectionData?.connectionId || connectionData?.id}
            />
          )
        )}
      </div>
    );
  }

  return (
    <IntegrationDetailShell
      integrationName={provider.label}
      connected={isConnected}
      credentialOk={isConnected}
      externalId={connectionData?.id || salesforceIntegration?.[0]?.id || null}
      lastSync={connectedDateLabel}
      reconnectLoading={integrationLoading || isCheckingConnection}
      reconnectLabel={isConnected ? "Reconnect" : "Connect"}
      onReconnect={handleConnectOrReconnect}
      onBack={handleBack}
      showMappingStudioCta
      setupComplete={!!salesforceIntegration?.[0]?.setup_complete}
      onOpenMappingStudio={handleOpenMappingStudio}
    >
      {!isConnected ? (
        <div className="rounded-lg border border-solid border-[var(--mantine-color-default-border)] p-4">
          <p className="text-sm text-[var(--mantine-color-text)]">
            Connect Salesforce to sync accounts, contacts, deals, notes, tasks, and emails.
          </p>
          <p className="mt-1 text-xs text-[var(--mantine-color-dimmed)]">
            Once connected, complete sync mappings below.
          </p>
        </div>
      ) : null}
    </IntegrationDetailShell>
  );
};

export default SalesforcePage;
